import React from 'react';
import classnames from 'classnames';
import { white } from '@fiverr-private/sass/helpers';
import { CloseThickIcon } from '@fiverr-private/icons';
import getRenderable from '../../../lib/getRenderable';
import {
    BORDER_RADIUSES,
    COLORS,
    THEMES,
    SIZES,
    COLORS_MAP_TO_VALUES,
    DISABLED_STYLES
} from './constants';
import classes from './index.module.scss';
import { isThemedColor } from './utils/isThemedColor';
import { LinkEvents, Props } from './types';

/**
 * @deprecated Please use Hightlight or Tag or Badge or Status from Data Display components instead.
 */
const Tag = ({
    children,
    handleDismiss,
    className: customClassName,
    href,
    color = 'grey',
    borderRadius = 'full',
    kind = 'full',
    theme = kind,
    size = 'tiny',
    disabled = false
}: Props) => {
    const isTiny = size === 'tiny';
    const isLink = !isTiny && href;
    const Element = isLink ? 'a' : 'span';

    const isDismissHandlerValid = typeof handleDismiss === 'function' && !isLink;

    const colorStyles = isThemedColor(color, theme) ? COLORS_MAP_TO_VALUES[color][theme] : COLORS_MAP_TO_VALUES[color];

    const fullTextColor = 'textColor' in colorStyles && colorStyles.textColor
        ? colorStyles.textColor
        : white;

    const hollowTextColor = 'textColor' in colorStyles && colorStyles.textColor
        ? colorStyles.textColor
        : colorStyles.default;

    const textColor = theme === 'hollow' ? hollowTextColor : fullTextColor;
    const className = classnames(
        classes.tag,
        customClassName,
        classes[`border-radius-${borderRadius}`],
        classes[`size-${size}`],
        { [classes.link]: href }
    );
    const defaultStyles = disabled
        ? DISABLED_STYLES[theme]
        : {
            borderColor: colorStyles.default,
            backgroundColor: theme === 'full' ? colorStyles.default : white,
            color: textColor
        };

    const props: React.AnchorHTMLAttributes<HTMLAnchorElement>= {
        style: defaultStyles
    };

    if (isLink) {
        props.href = href;

        const setDefaultStyles = (e: LinkEvents) => {
            const target = e.target as HTMLAnchorElement;

            target.style.color = defaultStyles.color;
            target.style.borderColor = defaultStyles.borderColor;

            if ('backgroundColor' in defaultStyles && defaultStyles.backgroundColor) {
                target.style.backgroundColor = defaultStyles.backgroundColor;
            }
        };

        const setMouseEventStyles = (e: LinkEvents, eventType: 'hover' | 'active') => {
            const target = e.target as HTMLAnchorElement;

            if (theme === 'hollow'){
                target.style.color = fullTextColor;
            }

            target.style.backgroundColor = colorStyles[eventType];
            target.style.borderColor = colorStyles[eventType];
        };

        props.onMouseEnter = (e) => setMouseEventStyles(e, 'hover');
        props.onMouseLeave = setDefaultStyles;
        props.onFocus = (e) => setMouseEventStyles(e, 'active');
        props.onBlur = setDefaultStyles;
    }

    return (
        <Element className={className} {...props}>
            {getRenderable(children)}
            {isDismissHandlerValid && (
                <button onClick={handleDismiss}
                    className={classnames(classes.dismiss, { [classes.sizeTiny]: isTiny })}>
                    <CloseThickIcon color={textColor}/>
                </button>
            )}
        </Element>
    );
};

Tag.displayName = 'Tag';

Object.assign(Tag, {
    BORDER_RADIUSES,
    COLORS,
    KINDS: THEMES,
    THEMES,
    SIZES
});

export default Tag;
